import { useState, useMemo } from 'react'
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  TrendingUp,
  Calculator,
  ArrowUpDown,
} from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'
import { CHART_TOOLTIP_PROPS, chartAxisPropsMinimal, chartGridProps, chartSeriesColor } from '@/config/chartTheme'
import { useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'
import { RATE_LABELS, sofrOf, useLiveRates } from '@/services/marketData'
// ── Types ──────────────────────────────────────────────────────────────────
type HedgeStatus = 'Hedged' | 'Unhedged' | 'Expiring'
type HedgeType = 'Cap' | 'Collar' | 'Swap'

interface FloatingDeal {
  deal: string
  property: string
  amount: number
  spread: number
  currentSOFR: number
  capStrike: number | null
  capExpiry: string | null
  capCost: number | null
  capMTM: number | null
  hedgeStatus: HedgeStatus
}

interface ActiveHedge {
  id: string
  type: HedgeType
  notional: number
  strikeOrFixed: number
  floor?: number
  start: string
  expiry: string
  mtm: number
  counterparty: string
  dealRef: string
}

interface SOFRDataPoint {
  month: string
  rate: number
}

interface ScenarioRow {
  sofrLevel: string
  sofr: number
  portfolioDSCR: number
  impactBps: number
  status: 'pass' | 'watch' | 'fail'
}

// ── Mock Data ──────────────────────────────────────────────────────────────
const CURRENT_SOFR = 4.33

const FLOATING_DEALS: FloatingDeal[] = [
  { deal: 'ALG-1001', property: 'Harbor Point Residences', amount: 62.5, spread: 375, currentSOFR: CURRENT_SOFR, capStrike: 4.50, capExpiry: '2027-04-01', capCost: 1.82, capMTM: 0.94, hedgeStatus: 'Hedged' },
  { deal: 'ALG-1003', property: 'Gateway Logistics Center', amount: 38.0, spread: 325, currentSOFR: CURRENT_SOFR, capStrike: 5.00, capExpiry: '2027-06-15', capCost: 1.15, capMTM: 0.42, hedgeStatus: 'Hedged' },
  { deal: 'ALG-1005', property: 'Meridian Business Park', amount: 29.5, spread: 350, currentSOFR: CURRENT_SOFR, capStrike: 4.00, capExpiry: '2026-06-30', capCost: 2.10, capMTM: 2.85, hedgeStatus: 'Expiring' },
  { deal: 'ALG-1007', property: 'Bayside Mixed-Use', amount: 44.0, spread: 400, currentSOFR: CURRENT_SOFR, capStrike: 5.50, capExpiry: '2028-01-15', capCost: 0.78, capMTM: 0.18, hedgeStatus: 'Hedged' },
  { deal: 'ALG-1009', property: 'Lakeshore Apartments', amount: 51.0, spread: 360, currentSOFR: CURRENT_SOFR, capStrike: 4.75, capExpiry: '2026-08-01', capCost: 1.45, capMTM: 1.12, hedgeStatus: 'Expiring' },
  { deal: 'ALG-1011', property: 'Crossroads Retail Center', amount: 22.0, spread: 425, currentSOFR: CURRENT_SOFR, capStrike: null, capExpiry: null, capCost: null, capMTM: null, hedgeStatus: 'Unhedged' },
  { deal: 'ALG-1013', property: 'Summit Office Tower', amount: 67.0, spread: 310, currentSOFR: CURRENT_SOFR, capStrike: 4.25, capExpiry: '2027-09-01', capCost: 1.95, capMTM: 1.58, hedgeStatus: 'Hedged' },
  { deal: 'ALG-1015', property: 'Parkview Senior Living', amount: 35.5, spread: 380, currentSOFR: CURRENT_SOFR, capStrike: null, capExpiry: null, capCost: null, capMTM: null, hedgeStatus: 'Unhedged' },
  { deal: 'ALG-1017', property: 'Riverfront Industrial', amount: 41.0, spread: 340, currentSOFR: CURRENT_SOFR, capStrike: 4.50, capExpiry: '2027-12-01', capCost: 1.62, capMTM: 0.88, hedgeStatus: 'Hedged' },
  { deal: 'ALG-1019', property: 'Uptown Multifamily', amount: 55.0, spread: 365, currentSOFR: CURRENT_SOFR, capStrike: 5.25, capExpiry: '2028-03-15', capCost: 0.92, capMTM: 0.26, hedgeStatus: 'Hedged' },
]

const ACTIVE_HEDGES: ActiveHedge[] = [
  { id: 'HC-001', type: 'Cap', notional: 62.5, strikeOrFixed: 4.50, start: '2025-04-01', expiry: '2027-04-01', mtm: 0.94, counterparty: 'JPMorgan Chase', dealRef: 'ALG-1001' },
  { id: 'HC-002', type: 'Cap', notional: 38.0, strikeOrFixed: 5.00, start: '2025-06-15', expiry: '2027-06-15', mtm: 0.42, counterparty: 'Wells Fargo', dealRef: 'ALG-1003' },
  { id: 'HC-003', type: 'Cap', notional: 29.5, strikeOrFixed: 4.00, start: '2024-06-30', expiry: '2026-06-30', mtm: 2.85, counterparty: 'Bank of America', dealRef: 'ALG-1005' },
  { id: 'HC-004', type: 'Collar', notional: 44.0, strikeOrFixed: 5.50, floor: 3.25, start: '2026-01-15', expiry: '2028-01-15', mtm: 0.18, counterparty: 'Goldman Sachs', dealRef: 'ALG-1007' },
  { id: 'HC-005', type: 'Cap', notional: 51.0, strikeOrFixed: 4.75, start: '2024-08-01', expiry: '2026-08-01', mtm: 1.12, counterparty: 'Citibank', dealRef: 'ALG-1009' },
  { id: 'HC-006', type: 'Cap', notional: 67.0, strikeOrFixed: 4.25, start: '2025-09-01', expiry: '2027-09-01', mtm: 1.58, counterparty: 'Morgan Stanley', dealRef: 'ALG-1013' },
  { id: 'HC-007', type: 'Cap', notional: 41.0, strikeOrFixed: 4.50, start: '2025-12-01', expiry: '2027-12-01', mtm: 0.88, counterparty: 'JPMorgan Chase', dealRef: 'ALG-1017' },
  { id: 'HC-008', type: 'Cap', notional: 55.0, strikeOrFixed: 5.25, start: '2026-03-15', expiry: '2028-03-15', mtm: 0.26, counterparty: 'Wells Fargo', dealRef: 'ALG-1019' },
]

const SOFR_HISTORY: SOFRDataPoint[] = [
  { month: 'Apr 25', rate: 4.32 },
  { month: 'May 25', rate: 4.30 },
  { month: 'Jun 25', rate: 4.28 },
  { month: 'Jul 25', rate: 4.18 },
  { month: 'Aug 25', rate: 4.08 },
  { month: 'Sep 25', rate: 3.95 },
  { month: 'Oct 25', rate: 3.88 },
  { month: 'Nov 25', rate: 3.92 },
  { month: 'Dec 25', rate: 4.05 },
  { month: 'Jan 26', rate: 4.18 },
  { month: 'Feb 26', rate: 4.27 },
  { month: 'Mar 26', rate: 4.33 },
]

const RATE_SCENARIOS: ScenarioRow[] = [
  { sofrLevel: '3.50%', sofr: 3.50, portfolioDSCR: 1.62, impactBps: 28, status: 'pass' },
  { sofrLevel: '4.00%', sofr: 4.00, portfolioDSCR: 1.48, impactBps: 14, status: 'pass' },
  { sofrLevel: '4.33%', sofr: 4.33, portfolioDSCR: 1.38, impactBps: 0, status: 'pass' },
  { sofrLevel: '4.50%', sofr: 4.50, portfolioDSCR: 1.32, impactBps: -6, status: 'pass' },
  { sofrLevel: '5.00%', sofr: 5.00, portfolioDSCR: 1.18, impactBps: -20, status: 'watch' },
  { sofrLevel: '5.50%', sofr: 5.50, portfolioDSCR: 1.06, impactBps: -32, status: 'watch' },
  { sofrLevel: '6.00%', sofr: 6.00, portfolioDSCR: 0.94, impactBps: -44, status: 'fail' },
]

const CAP_STRIKES = [4.00, 4.25, 4.50, 4.75, 5.00, 5.25, 5.50]

// ── Helpers ────────────────────────────────────────────────────────────────
const fmt = (n: number, decimals = 1) => n.toFixed(decimals)
const fmtM = (n: number) => `$${n.toFixed(1)}M`
const fmtPct = (n: number) => `${n.toFixed(2)}%`
const fmtBps = (n: number) => `${n > 0 ? '+' : ''}${n}`

function daysUntil(date: string | null) {
  if (!date) return Infinity
  const diff = new Date(date).getTime() - new Date().getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

function statusBg(status: HedgeStatus) {
  if (status === 'Hedged') return 'bg-emerald-400/10 border-emerald-400/20 text-emerald-300'
  if (status === 'Expiring') return 'bg-amber-400/10 border-amber-400/20 text-amber-300'
  return 'bg-red-400/10 border-red-400/20 text-red-300'
}

// ── Component ──────────────────────────────────────────────────────────────
/**
 * Everything above is the illustrative book: a $445.5M floating-rate book,
 * caps and a collar with named bank counterparties, and a hardcoded "current"
 * SOFR of 4.33% — shown on live accounts beside a live print of 3.87%, with a
 * row named like a live pipeline deal (MOD-05). The live partition has no
 * hedge records, so it gets the real SOFR print and a statement of what is
 * not recorded; the demo keeps the sample under a label.
 */
export default function HedgeDesk() {
  const demoEnabled = useModuleDemoEnabled('hedge-desk')
  return demoEnabled ? <HedgeDeskSample /> : <HedgeDeskPending />
}

function HedgeDeskPending() {
  const rates = useLiveRates()
  const sofr = sofrOf(rates.data)
  return (
    <div className="space-y-3 p-4">
      <section className="card px-5 py-4">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-amber-400" />
          <span className="terminal-kicker">Hedge Desk</span>
        </div>
        <div className="mt-3 flex flex-wrap items-baseline gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-stone-500">
            {RATE_LABELS.SOFR_ON.label}
          </span>
          {sofr ? (
            <>
              <span className="text-lg font-semibold text-amber-300">{(sofr.rate * 100).toFixed(2)}%</span>
              <span className="text-[11px] text-stone-500">
                {RATE_LABELS.SOFR_ON.source}
                {sofr.asOf ? ` · as of ${sofr.asOf}` : ''}
              </span>
            </>
          ) : (
            <span className="text-[11px] text-stone-500">
              {rates.isLoading ? 'Loading…' : 'The public rates feed did not answer; no rate is shown.'}
            </span>
          )}
        </div>
        <p className="mt-3 max-w-3xl text-[12px] leading-relaxed text-stone-400">
          No hedge positions are recorded on this account: the platform holds no interest-rate cap, collar or swap
          records, counterparties or mark-to-market source. Exposure, hedge coverage, P&amp;L, expiries and rate
          scenarios appear here once hedge positions are recorded against funded floating-rate loans.
        </p>
      </section>
    </div>
  )
}

function HedgeDeskSample() {
  const [calcNotional, setCalcNotional] = useState(50)
  const [calcTerm, setCalcTerm] = useState(3)
  const [calcStrike, setCalcStrike] = useState(5.0)
  const [calcSOFR] = useState(CURRENT_SOFR)

  // ── Computed Metrics ──────────────────────────────────────────────────
  const totalFloating = FLOATING_DEALS.reduce((s, d) => s + d.amount, 0)
  const hedgedDeals = FLOATING_DEALS.filter((d) => d.hedgeStatus === 'Hedged')
  const totalFixed = hedgedDeals.reduce((s, d) => s + d.amount, 0) + FLOATING_DEALS.filter((d) => d.hedgeStatus === 'Expiring').reduce((s, d) => s + d.amount, 0)
  const hedgedPct = ((totalFixed / totalFloating) * 100).toFixed(1)
  const avgCapStrike = FLOATING_DEALS.filter((d) => d.capStrike !== null).reduce((s, d) => s + d.capStrike!, 0) / FLOATING_DEALS.filter((d) => d.capStrike !== null).length
  const avgCapCost = FLOATING_DEALS.filter((d) => d.capCost !== null).reduce((s, d) => s + d.capCost!, 0) / FLOATING_DEALS.filter((d) => d.capCost !== null).length
  const portfolioDuration = 2.8
  const unrealizedPnL = ACTIVE_HEDGES.reduce((s, h) => s + h.mtm, 0)
  const nextExpiry = Math.min(...FLOATING_DEALS.filter((d) => d.capExpiry).map((d) => daysUntil(d.capExpiry)))

  // ── Cap Calculator ────────────────────────────────────────────────────
  const calcPremium = useMemo(() => {
    const vol = 0.42
    const timeValue = Math.sqrt(calcTerm)
    const moneyness = Math.max(0, calcSOFR - calcStrike) + 0.5
    return calcNotional * vol * timeValue * moneyness * 0.01 * (1 + (calcStrike - 4) * 0.15)
  }, [calcNotional, calcTerm, calcStrike, calcSOFR])

  const annualizedCost = calcPremium / calcTerm
  const breakevenSOFR = calcStrike + (calcPremium / calcNotional) * 100
  const maxExposure = calcNotional * (calcStrike + (375 / 100)) * 0.01

  const metrics = [
    { label: 'Total Floating Exposure', value: fmtM(totalFloating) },
    { label: 'Total Hedged', value: fmtM(totalFixed) },
    { label: 'Hedged %', value: `${hedgedPct}%` },
    { label: 'Wtd Avg Cap Strike', value: fmtPct(avgCapStrike) },
    { label: 'Avg Cap Cost', value: fmtM(avgCapCost) },
    { label: 'Portfolio Duration', value: `${portfolioDuration}y` },
    { label: 'Unrealized P&L', value: fmtM(unrealizedPnL) },
    { label: 'Next Expiry', value: `${nextExpiry}d` },
  ]

  return (
    <div className="page-fill gap-2 p-3">
        <div className="flex-shrink-0 rounded-md border border-amber-400/15 bg-amber-400/[0.05] px-3 py-1.5 text-[11px] text-amber-200/80">
          Sample data — an illustrative hedge book; the {fmtPct(CURRENT_SOFR)} SOFR here is a sample figure, not today&apos;s print.
        </div>
        {/* ── Metric Strip ──────────────────────────────────────────────── */}
        <div className="grid flex-shrink-0 grid-cols-8 gap-2">
          {metrics.map((m) => (
            <div key={m.label} className="metric-cell">
              <div className="metric-cell-label">{m.label}</div>
              <div className="metric-cell-value text-[15px]">{m.value}</div>
            </div>
          ))}
        </div>

        {/* ── Main Split ────────────────────────────────────────────────── */}
        <div className="grid min-h-0 flex-1 grid-cols-[55fr_45fr] gap-2 overflow-hidden">
          {/* LEFT: Portfolio Exposure */}
          <div className="panel flex min-h-0 flex-col overflow-hidden">
            <div className="panel-header">
              <span className="panel-header-title">Portfolio Floating Rate Exposure</span>
              <span className="text-[10px] font-mono text-stone-500">{FLOATING_DEALS.length} positions</span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Deal</th>
                    <th>Property</th>
                    <th className="text-right">Amount</th>
                    <th className="text-right">Spread</th>
                    <th className="text-right">SOFR</th>
                    <th className="text-right">All-in</th>
                    <th className="text-right">Cap Strike</th>
                    <th>Cap Expiry</th>
                    <th className="text-right">Cap Cost</th>
                    <th className="text-right">MTM</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {FLOATING_DEALS.map((d) => {
                    const allIn = d.currentSOFR + d.spread / 100
                    return (
                      <tr key={d.deal} className={d.hedgeStatus === 'Unhedged' ? 'bg-red-400/[0.03]' : d.hedgeStatus === 'Expiring' ? 'bg-amber-400/[0.03]' : ''}>
                        <td className="font-mono text-[11px] text-amber-300/80">{d.deal}</td>
                        <td className="text-stone-200 max-w-[140px] truncate">{d.property}</td>
                        <td className="text-right font-mono">{fmtM(d.amount)}</td>
                        <td className="text-right font-mono text-stone-400">+{d.spread}</td>
                        <td className="text-right font-mono">{fmtPct(d.currentSOFR)}</td>
                        <td className="text-right font-mono text-stone-200">{fmtPct(allIn)}</td>
                        <td className="text-right font-mono">{d.capStrike ? fmtPct(d.capStrike) : <span className="text-stone-600">--</span>}</td>
                        <td className="font-mono text-[11px]">{d.capExpiry ?? <span className="text-stone-600">--</span>}</td>
                        <td className="text-right font-mono">{d.capCost !== null ? fmtM(d.capCost) : <span className="text-stone-600">--</span>}</td>
                        <td className="text-right font-mono">{d.capMTM !== null ? fmtM(d.capMTM) : <span className="text-stone-600">--</span>}</td>
                        <td>
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusBg(d.hedgeStatus)}`}>
                            {d.hedgeStatus === 'Hedged' && <CheckCircle2 size={10} />}
                            {d.hedgeStatus === 'Expiring' && <Clock3 size={10} />}
                            {d.hedgeStatus === 'Unhedged' && <AlertTriangle size={10} />}
                            {d.hedgeStatus}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* RIGHT: Hedge Analytics */}
          <div className="flex min-h-0 flex-col gap-2 overflow-hidden">
            {/* Rate Scenario Analysis */}
            <div className="panel">
              <div className="panel-header">
                <span className="panel-header-title">Rate Scenario Analysis</span>
                <ArrowUpDown size={12} className="text-stone-500" />
              </div>
              <div className="overflow-x-auto">
                <table className="dense-table">
                  <thead>
                    <tr>
                      <th>SOFR Level</th>
                      <th className="text-right">Portfolio DSCR</th>
                      <th className="text-right">Impact (bps)</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RATE_SCENARIOS.map((s) => (
                      <tr key={s.sofrLevel} className={s.sofr === CURRENT_SOFR ? 'bg-amber-400/[0.06]' : ''}>
                        <td className="font-mono">
                          {s.sofrLevel}
                          {s.sofr === CURRENT_SOFR && <span className="ml-1.5 text-[9px] text-amber-400">(current)</span>}
                        </td>
                        <td className={`text-right font-mono ${s.portfolioDSCR < 1.0 ? 'text-red-400' : s.portfolioDSCR < 1.15 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {fmt(s.portfolioDSCR, 2)}x
                        </td>
                        <td className={`text-right font-mono ${s.impactBps < 0 ? 'text-red-300' : s.impactBps > 0 ? 'text-emerald-300' : 'text-stone-400'}`}>
                          {fmtBps(s.impactBps)}
                        </td>
                        <td>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            s.status === 'pass' ? 'bg-emerald-400/10 text-emerald-300' :
                            s.status === 'watch' ? 'bg-amber-400/10 text-amber-300' :
                            'bg-red-400/10 text-red-300'
                          }`}>
                            {s.status.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Interest Rate Cap Calculator */}
            <div className="panel">
              <div className="panel-header">
                <span className="panel-header-title">Interest Rate Cap Calculator</span>
                <Calculator size={12} className="text-stone-500" />
              </div>
              <div className="panel-body">
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div>
                    <label className="block text-[9px] font-mono uppercase tracking-widest text-stone-500 mb-1">Notional ($M)</label>
                    <input
                      type="number"
                      value={calcNotional}
                      onChange={(e) => setCalcNotional(Number(e.target.value))}
                      className="input w-full text-[12px] py-1.5 px-2"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-mono uppercase tracking-widest text-stone-500 mb-1">Term (years)</label>
                    <input
                      type="number"
                      value={calcTerm}
                      onChange={(e) => setCalcTerm(Number(e.target.value))}
                      className="input w-full text-[12px] py-1.5 px-2"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-mono uppercase tracking-widest text-stone-500 mb-1">Strike Rate (%)</label>
                    <input
                      type="number"
                      step="0.25"
                      value={calcStrike}
                      onChange={(e) => setCalcStrike(Number(e.target.value))}
                      className="input w-full text-[12px] py-1.5 px-2"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-mono uppercase tracking-widest text-stone-500 mb-1">Current SOFR</label>
                    <div className="input w-full text-[12px] py-1.5 px-2 text-stone-400">{fmtPct(calcSOFR)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="metric-cell">
                    <div className="metric-cell-label">Est. Premium</div>
                    <div className="text-[14px] font-semibold text-amber-200">{fmtM(calcPremium)}</div>
                  </div>
                  <div className="metric-cell">
                    <div className="metric-cell-label">Annual Cost</div>
                    <div className="text-[14px] font-semibold text-stone-200">{fmtM(annualizedCost)}</div>
                  </div>
                  <div className="metric-cell">
                    <div className="metric-cell-label">Breakeven SOFR</div>
                    <div className="text-[14px] font-semibold text-stone-200">{fmtPct(breakevenSOFR)}</div>
                  </div>
                  <div className="metric-cell">
                    <div className="metric-cell-label">Max Exposure</div>
                    <div className="text-[14px] font-semibold text-stone-200">{fmtM(maxExposure)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Hedges Summary */}
            <div className="panel flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="panel-header">
                <span className="panel-header-title">Active Hedges</span>
                <Shield size={12} className="text-stone-500" />
              </div>
              <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
                <table className="dense-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th className="text-right">Notional</th>
                      <th className="text-right">Strike/Fixed</th>
                      <th>Start</th>
                      <th>Expiry</th>
                      <th className="text-right">MTM</th>
                      <th>Counterparty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ACTIVE_HEDGES.map((h) => {
                      const expiryDays = daysUntil(h.expiry)
                      return (
                        <tr key={h.id}>
                          <td>
                            <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${
                              h.type === 'Cap' ? 'border-blue-400/20 bg-blue-400/10 text-blue-300' :
                              h.type === 'Collar' ? 'border-purple-400/20 bg-purple-400/10 text-purple-300' :
                              'border-emerald-400/20 bg-emerald-400/10 text-emerald-300'
                            }`}>
                              {h.type}
                            </span>
                          </td>
                          <td className="text-right font-mono">{fmtM(h.notional)}</td>
                          <td className="text-right font-mono">
                            {fmtPct(h.strikeOrFixed)}
                            {h.floor && <span className="text-stone-500"> / {fmtPct(h.floor)}</span>}
                          </td>
                          <td className="font-mono text-[11px] text-stone-400">{h.start}</td>
                          <td className={`font-mono text-[11px] ${expiryDays < 180 ? 'text-amber-400' : 'text-stone-400'}`}>
                            {h.expiry}
                            {expiryDays < 180 && <span className="ml-1 text-[9px]">({expiryDays}d)</span>}
                          </td>
                          <td className={`text-right font-mono ${h.mtm >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtM(h.mtm)}</td>
                          <td className="text-stone-300 text-[11px]">{h.counterparty}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom: SOFR Rate Chart ───────────────────────────────────── */}
        <div className="panel flex h-[180px] flex-shrink-0 flex-col overflow-hidden">
          <div className="panel-header">
            <span className="panel-header-title">SOFR Rate History &amp; Cap Strike Overlay</span>
            <div className="flex items-center gap-3 text-[10px] text-stone-500">
              <span className="flex items-center gap-1"><TrendingUp size={10} className="text-amber-400" /> SOFR</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-px bg-emerald-400" /> Cap Strikes</span>
            </div>
          </div>
          <div className="min-h-0 flex-1 p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={SOFR_HISTORY} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid {...chartGridProps()} />
                <XAxis dataKey="month" {...chartAxisPropsMinimal()} />
                <YAxis domain={[3.6, 5.8]} {...chartAxisPropsMinimal()} tickFormatter={(v: number) => `${v.toFixed(1)}%`} />
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number) => [`${value.toFixed(2)}%`, 'SOFR']} />
                <Line type="monotone" dataKey="rate" stroke={chartSeriesColor(0)} strokeWidth={2} dot={{ fill: chartSeriesColor(0), r: 3 }} activeDot={{ r: 5, fill: chartSeriesColor(5) }} />
                {CAP_STRIKES.map((strike) => (
                  <ReferenceLine key={strike} y={strike} stroke="rgba(52,211,153,0.3)" strokeDasharray="4 4" label={{ value: `${strike.toFixed(2)}%`, position: 'right', fill: '#6ee7b7', fontSize: 9 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
    </div>
  )
}
